import { Formik } from 'formik'
import inRange from 'lodash/inRange'
import React, { useContext, useState } from 'react'
import * as Yup from 'yup'

import { ampli } from '@Analytics/ampli'
import CheckIcon from '@Assets/SVG/Check'
import { ButtonWithProgress } from '@Components/Button/ButtonWithProgress'
import { Form } from '@Components/FormElements/Formik'
import { TextArea } from '@Components/FormElements/Formik/TextArea'
import { TextField } from '@Components/FormElements/Formik/TextField'
import { SVGIcon } from '@Components/Icons'
import { NativeModal, NativeModalBody, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './ContactContent.module.scss'

interface ContactEmailResponse {
  status: 'OK' | 'ERROR'
}

type ContactFormState = 'default' | 'error' | 'loading' | 'success'

export const ContactContent = () => {
  const { t, tDanger } = useTranslation()
  const { store, storeURL } = useContext(MetadataContext)

  const storeEmail = store?.config?.email
  const [state, setState] = useState<ContactFormState>('default')

  const ContactSignupSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('validation:YOUR_EMAIL_IS_INVALID'))
      .required(t('validation:EMAIL_REQUIRED_TEXT')),
    message: Yup.string().required(t('validation:INPUT_FIELD_IS_REQUIRED')),
    name: Yup.string().required(t('validation:INPUT_FIELD_IS_REQUIRED')),
    subject: Yup.string().required(t('validation:INPUT_FIELD_IS_REQUIRED')),
  })

  const submitFormURL = storeURL('contacts/index/post/')

  const onSubmitForm = async ({
    email,
    message,
    name,
    orderNumber,
    subject,
  }: {
    email: string
    message: string
    name: string
    orderNumber?: string
    subject: string
  }) => {
    setState('loading')

    const data = new FormData()
    data.set('comment', message)
    data.set('email', email)
    data.set('name', name)
    data.set('orderid', orderNumber || '')
    data.set('subject', subject)

    let isSuccess = false

    try {
      const res = await fetch(submitFormURL, {
        body: data,
        method: 'post',
      })

      if (!inRange(res.status, 200, 400)) {
        throw new Error(`bad status code: ${res.status}`)
      }

      const result: ContactEmailResponse = JSON.parse(await res.text())
      if (result?.status !== 'OK') {
        throw new Error(`bad response: ${result?.status ?? 'invalid'}`)
      }

      setState('success')
      isSuccess = true
    } catch (e) {
      console.error('unhandled exception', e)
      setState('error')
    } finally {
      ampli.contactEmailSentRes({ is_success: isSuccess })

      const url = new URL(window?.location?.href)
      if (isSuccess && url.searchParams.get('return-broken') === '1') {
        ampli.returnBrokenEmailSent()
      }
    }
  }

  return (
    <>
      {state !== 'success' && (
        <>
          <div className={styles.title}>
            <h1>{t('contact:CONTACT_PAGE_TITLE')}</h1>
            <p className="subtitle">{t('contact:CONTACT_PAGE_INTRO_TEXT')}</p>
          </div>

          <div className={styles.contactArea}>
            <Formik
              initialValues={{
                email: '',
                message: '',
                name: '',
                orderNumber: '',
                subject: '',
              }}
              onSubmit={onSubmitForm}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={ContactSignupSchema}
            >
              {() => (
                <Form className="contactForm">
                  <TextField
                    id="th-contact-subject"
                    label={t('contact:CONTACT_PAGE_SUBJECT_PLACEHOLDER_LABEL')}
                    name="subject"
                  />
                  <TextArea
                    className={styles.noLabel}
                    id="th-contact-message"
                    label={t('contact:CONTACT_PAGE_MESSAGE_PLACEHOLDER_LABEL')}
                    name="message"
                  />
                  <TextField
                    id="th-contact-name"
                    label={t('contact:CONTACT_PAGE_NAME_PLACEHOLDER_LABEL')}
                    name="name"
                  />
                  <TextField
                    id="th-contact-email"
                    label={t('contact:FOOTER_SIGN_UP_YOUR_EMAIL')}
                    name="email"
                    type="email"
                  />
                  <TextField
                    id="th-contact-order-number"
                    label={t('contact:CONTACT_PAGE_ORDER_NUMBER_LABEL')}
                    name="orderNumber"
                  />
                  <div className={styles.buttonContainer}>
                    <ButtonWithProgress
                      loading={state === 'loading'}
                      type="submit"
                    >
                      {t('contact:SEND_EMAIL_LABEL')}
                    </ButtonWithProgress>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <div
            className={styles.sendUsEmailAt}
            {...tDanger('contact:OR_SEND_US_AN_EMAIL_AT_X', {
              arg1: `<a href="mailto:${storeEmail}">${storeEmail}</a>`,
            })}
          />
        </>
      )}
      {state === 'success' && (
        <div className={styles.container}>
          <div>
            <SVGIcon color="primary" icon={CheckIcon} size="50px" />
          </div>
          <div className={styles.displayMessage}>
            {t('common:SUCCESS_LABEL')}
            !
          </div>
          <div className={styles.text}>
            {t('contact:CONTACT_PAGE_SUCCESS_TITLE')}
            !
          </div>
          <div className={styles.text}>{t('contact:GET_BACK_ASAP')}</div>
        </div>
      )}
      <NativeModal defaultOpen={state === 'error'} onClose={() => setState('default')}>
        <NativeModalHeader />
        <NativeModalBody>
          {t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}
        </NativeModalBody>
      </NativeModal>
    </>
  )
}
